import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { storeFCMToken } from './api/pelaajaporssiApi';
import { Badge } from '@capawesome/capacitor-badge';


export const checkPermissions = async () => {
  const result = await FirebaseMessaging.checkPermissions();
  return result.receive;
};

export const requestPermissions = async () => {
  const result = await FirebaseMessaging.requestPermissions();
  return result.receive;
};

export const getToken = async (vapidKey?: string) => {
  if (vapidKey) {
    const result = await FirebaseMessaging.getToken({
      vapidKey
    });
    return result.token;
  } else {
    const result = await FirebaseMessaging.getToken();
    return result.token;
  }
};

export const getTokenSafely = async () => {
  try {
    const hasPermission = await checkPermissions();

    const prevResult = localStorage.getItem('notificationPermissionResult');

    if (prevResult && prevResult !== "granted") {
      return false;
    }

    if (hasPermission !== 'granted') {
      const permissionResult = await requestPermissions();

      localStorage.setItem('notificationPermissionResult', permissionResult);

      if (permissionResult !== 'granted') {
        throw new Error('Notification permissions not granted');
      }
    }

    await new Promise(resolve => setTimeout(resolve, 1000));

    const result = await FirebaseMessaging.getToken();
    return result.token;
  } catch (error) {
    console.error('Error getting FCM token:', error);

    if (error instanceof Error && error.message.includes('No APNS token')) {
      console.log('APNS token not ready, retrying in 3 seconds...');
      await new Promise(resolve => setTimeout(resolve, 3000));

      try {
        const result = await FirebaseMessaging.getToken();
        return result.token;
      } catch (retryError) {
        console.error('Retry failed:', retryError);
        throw retryError;
      }
    }

    throw error;
  }
};

export const deleteToken = async () => {
  await FirebaseMessaging.deleteToken();
};

export const getDeliveredNotifications = async () => {
  const result = await FirebaseMessaging.getDeliveredNotifications();
  return result.notifications;
};

export const removeDeliveredNotifications = async () => {
  await FirebaseMessaging.removeDeliveredNotifications({
    notifications: [
      {
        id: '798dfhliblqew89pzads',
      },
    ],
  });
};

export const removeAllDeliveredNotifications = async () => {
  await FirebaseMessaging.removeAllDeliveredNotifications();
  await Badge.clear();
};

export const subscribeToTopic = async () => {
  await FirebaseMessaging.subscribeToTopic({ topic: 'news' });
};

export const unsubscribeFromTopic = async () => {
  await FirebaseMessaging.unsubscribeFromTopic({ topic: 'news' });
};

export const addTokenReceivedListener = async () => {
  await FirebaseMessaging.addListener('tokenReceived', async (event) => {
    console.log('tokenReceived', { event });
    await storeFCMToken();
  });
};

export const addNotificationReceivedListener = async () => {
  await FirebaseMessaging.addListener('notificationReceived', event => {
    console.log('notificationReceived', { event });
  });
};

// Helper function for notification navigation logic
const navigateFromNotificationData = (data: any, navigate: (path: string) => void) => {
  if (!data) {
    navigate('/');
    return;
  }

  // Handle club invitation
  if (data.inviteId && data.clubId) {
    navigate(`/invitation/${data.clubId}/${data.inviteId}`);
  }
  // Handle event notification
  else if (data.eventId && data.clubId) {
    navigate(`/clubs/${data.clubId}/${data.eventId}`);
  }
  // Handle general club notification
  else if (data.clubId) {
    navigate(`/clubs/${data.clubId}`);
  }
  // Default fallback - go to notifications page
  else {
    navigate('/');
  }
};

export const addNotificationActionPerformedListener = async (navigate: (path: string) => void) => {
  await FirebaseMessaging.addListener('notificationActionPerformed', event => {
    console.log('notificationActionPerformed', { event });

    // Handle notification tap - navigate to appropriate page
    const notification = event.notification;
    const data = notification.data as any; // Type assertion for notification data

    navigateFromNotificationData(data, navigate);
  });
};

export const removeAllListeners = async () => {
  await FirebaseMessaging.removeAllListeners();
};

export const checkForInitialNotification = async (navigate: (path: string) => void) => {
  try {
    // Check if app was launched from a notification
    const result = await FirebaseMessaging.getDeliveredNotifications();

    // On iOS, when app is launched from notification, we need to check the launch options
    // This is handled by Capacitor automatically, but we can also check for any pending notifications
    console.log('Checking for initial notification', result);

    // If there are delivered notifications, we might want to handle the most recent one
    // However, this approach might not be reliable for determining app launch reason
    // The notificationActionPerformed listener should handle most cases

  } catch (error) {
    console.error('Error checking for initial notification:', error);
  }
};